import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, Loader2, Clock3, ArrowLeft, ArrowRight, Check } from "lucide-react";

// Defined once, outside the component, so the fallback is the SAME object on
// every render. A fresh `{}` each render made the effect below re-run forever
// whenever the page was opened without router state (refresh / direct visit).
const EMPTY_RESULT = {};
const EMPTY_FILES = [];

const CURRENT_STEP = 1;
const CIRCLE_CIRCUMFERENCE = 327;

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const ARTIFACT_STEPS = [
  "Upload Artifact",
  "Automatic Verification",
  "Artifact Integrity Analysis",
  "Policy & Key Validation",
  "Verification Result",
];

const DOCUMENT_STEPS = [
  "Upload Document",
  "Automatic Verification",
  "Tamper Detection",
  "Policy & Key Validation",
  "Verification Result",
];

const STEP_CIRCLE_CLASS = {
  done: "bg-green-500 border-green-500 text-white",
  current: "bg-blue-600 border-blue-600 text-white",
  upcoming: "bg-white border-gray-300 text-gray-500",
};

const STEP_LABEL_CLASS = {
  done: "text-green-600",
  current: "text-blue-600",
  upcoming: "text-gray-500",
};

const CHECK_ICON = {
  completed: <CheckCircle2 size={18} className="text-green-500" />,
  running: <Loader2 size={18} className="text-blue-500 animate-spin" />,
  pending: <Clock3 size={18} className="text-gray-300" />,
  failed: <div className="text-red-500 font-bold text-sm">✕</div>,
};

const CHECK_LABEL = {
  completed: { text: "Completed", className: "text-green-600" },
  running: { text: "In Progress", className: "text-blue-600" },
  pending: { text: "Pending", className: "text-gray-400" },
  failed: { text: "Failed", className: "text-red-600" },
  skipped: { text: "Not Enabled", className: "text-orange-500" },
};

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                                */
/* -------------------------------------------------------------------------- */

function getSteps(isArtifact) {
  return isArtifact ? ARTIFACT_STEPS : DOCUMENT_STEPS;
}

function getStepState(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

// null means the check isn't enabled. `canSkip` lets a caller decide whether
// null counts as "skipped" or falls through to failed like any falsy value.
function resultToStatus(value, canSkip = true) {
  if (canSkip && value === null) return "skipped";
  return value ? "completed" : "failed";
}

function buildVerificationChecks(result, isArtifact) {
  return [
    {
      title: isArtifact ? "Artifact Integrity" : "Document Integrity",
      status: resultToStatus(result.merkle_valid, false),
    },
    {
      title: isArtifact ? "Signature Verification" : "PQ Signature Verification",
      status: resultToStatus(result.pq_signature_valid, false),
    },
    {
      title: "Blockchain Verification",
      status: resultToStatus(result.blockchain_valid, isArtifact),
    },
    {
      title: "Timestamp Verification",
      status: resultToStatus(result.tsa_valid),
    },
    {
      title: "Certificate Verification",
      status: resultToStatus(result.certificate_valid),
    },
  ];
}

function computeScore(result, checks) {
  if (typeof result.verification_score === "number") return result.verification_score;
  return checks.some((c) => c.status === "failed") ? 50 : 75;
}

function getStatusMessage({ hasFile, isVerifying, isValid, noun, isArtifact }) {
  if (!hasFile) {
    return {
      title: `No ${noun} Found`,
      detail: `Please go back and upload ${isArtifact ? "an artifact" : "a document"}.`,
    };
  }
  if (isVerifying) {
    return { title: `Verifying ${noun}...`, detail: "This may take a few seconds." };
  }
  if (isValid) {
    return {
      title: "Verification Completed!",
      detail: `The ${noun.toLowerCase()} is authentic and verified.`,
    };
  }
  return { title: "Some Checks Failed", detail: "One or more checks did not pass." };
}

/* -------------------------------------------------------------------------- */
/* Presentational components                                                   */
/* -------------------------------------------------------------------------- */

function StepProgress({ steps, currentStep }) {
  return (
    <div className="flex items-center justify-between mb-2 h-[72px]">
      {steps.map((step, index) => {
        const state = getStepState(index, currentStep);
        const isLast = index === steps.length - 1;

        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {!isLast && (
              <div
                className={`absolute top-5 left-1/2 w-full h-[2px] ${
                  state === "done" ? "bg-green-500" : "bg-gray-300"
                }`}
              />
            )}
            <div
              className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${STEP_CIRCLE_CLASS[state]}`}
            >
              {state === "done" ? <Check size={12} /> : index + 1}
            </div>
            <p
              className={`mt-1 text-sm font-medium text-center hidden sm:block ${STEP_LABEL_CLASS[state]}`}
            >
              {step}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function ProgressCircle({ progress, title, detail }) {
  return (
    <div className="flex flex-col items-center justify-center mb-4">
      <div className="relative w-[150px] h-[150px]">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" stroke="#e5e7eb" strokeWidth="8" fill="none" />
          <circle
            cx="60" cy="60" r="52"
            stroke="url(#grad)" strokeWidth="8" fill="none"
            strokeLinecap="round"
            strokeDasharray={CIRCLE_CIRCUMFERENCE}
            strokeDashoffset={CIRCLE_CIRCUMFERENCE - (CIRCLE_CIRCUMFERENCE * progress) / 100}
            className="transition-all duration-700"
          />
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#c4b5fd" />
              <stop offset="100%" stopColor="#2563eb" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <h2 className="text-[32px] font-bold text-gray-800">{progress}%</h2>
        </div>
      </div>
      <div className="text-center mt-3">
        <h2 className="text-16px  text-gray-800">{title}</h2>
        <p className="text-gray-500 mt-1 text-14px">{detail}</p>
      </div>
    </div>
  );
}

function CheckRow({ item }) {
  const label = CHECK_LABEL[item.status];

  return (
    <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2 bg-white">
      <div className="flex items-center gap-3">
        {CHECK_ICON[item.status]}
        <p className="text-gray-800 text-14px">{item.title}</p>
      </div>
      <div>
        {label && <span className={`${label.className} font-semibold text-sm`}>{label.text}</span>}
      </div>
    </div>
  );
}

function ChecksPanel({ checks }) {
  return (
    <div className="bg-gray-50 border rounded-2xl p-3 mb-4 space-y-6">
      <h3 className="text-20px font-semibold text-gray-700 mb-2">Verification Checks</h3>
      <div className="space-y-2">
        {checks.map((item) => (
          <CheckRow key={item.title} item={item} />
        ))}
      </div>
    </div>
  );
}

function FooterButtons({ onCancel, onNext }) {
  return (
    <div className="flex justify-between mt-3">
      <button
        onClick={onCancel}
        className="inline-flex items-center gap-1.5 h-[44px] px-6 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium"
      >
        <ArrowLeft size={14} /> Cancel
      </button>
      <button
        onClick={onNext}
        className="flex items-center gap-1.5 h-[44px] px-6 rounded-[10px] text-white text-sm font-medium bg-[#2563EB] hover:bg-[#1D4ED8] transition"
      >
        Next <ArrowRight size={14} />
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page component                                                              */
/* -------------------------------------------------------------------------- */

export default function AutomaticVerification() {
  const navigate = useNavigate();
  const location = useLocation();

  const verificationResult = location.state?.verificationResult || EMPTY_RESULT;

  const uploadedFiles = location.state?.uploadedFiles || EMPTY_FILES;
  const hasFile = uploadedFiles.length > 0;

  // Artifact verification reuses this wizard; the adapter sets kind: "artifact".
  const isArtifact = verificationResult.kind === "artifact";
  const noun = isArtifact ? "Artifact" : "Document";
  const steps = getSteps(isArtifact);

  const [progress, setProgress] = useState(0);
  const [checks, setChecks] = useState([]);

  useEffect(() => {
    // Nothing to show when the page is opened without a result.
    if (verificationResult === EMPTY_RESULT) return;

    const verificationChecks = buildVerificationChecks(verificationResult, isArtifact);
    setProgress(computeScore(verificationResult, verificationChecks));
    setChecks(verificationChecks);
  }, [verificationResult, isArtifact]);

  const handleNext = () => {
    if (checks.length === 0) {
      alert("Please wait until verification is complete.");
      return;
    }
    navigate(isArtifact ? "/artifact-integrity" : "/ai-tamper-detection", {
      state: { uploadedFiles, verificationResult },
    });
  };

  const handleCancel = () => {
    navigate(isArtifact ? "/artifact-verification" : "/verification");
  };

  const { title, detail } = getStatusMessage({
    hasFile,
    isVerifying: checks.length === 0,
    isValid: Boolean(verificationResult.document_valid),
    noun,
    isArtifact,
  });

  return (
    <div className="w-full bg-white mx-auto">
      <StepProgress steps={steps} currentStep={CURRENT_STEP} />
      <ProgressCircle progress={progress} title={title} detail={detail} />
      <ChecksPanel checks={checks} />
      <FooterButtons onCancel={handleCancel} onNext={handleNext} />
    </div>
  );
}